import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Team, TeamFormValues } from '../interfaces'

import { useSidebarPrivileges } from '@entities/viewer'
import { AppRoute } from '@shared/config'

import { useTeamsStore } from './store'
import { useTeamDelete } from '../../api/teams/delete.hook'
import { useTeamSave } from '../../api/teams/save.hook'
import { useTeamApi } from '../../api/teams/show.hook'
import { toMemberOptions } from './team-options'

interface TeamEditReturn {
  refName: Ref<string>
  refMembers: Ref<number[]>
  memberOptions: ComputedRef<AppSelectOption<number>[]>
  team: ComputedRef<Team | null>
  isSaving: ComputedRef<boolean>
  isNotFound: Ref<boolean>
  isSaveDisabled: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  isDeleteOpen: Ref<boolean>
  load: () => Promise<void>
  save: () => Promise<void>
  askDelete: () => void
  cancelDelete: () => void
  confirmDelete: () => Promise<void>
}

export const useTeamEditPage = (): TeamEditReturn => {
  const route = useRoute()
  const router = useRouter()
  const store = useTeamsStore()
  const { canManageTeams } = useSidebarPrivileges()

  const teamId = String(route.params.id)

  const refName = ref<string>('')
  const refMembers = ref<number[]>([])
  const isNotFound = ref<boolean>(false)
  const isDeleteOpen = ref<boolean>(false)

  /** Snapshot of the last persisted values — the Save button stays disabled until something differs. */
  const original = ref<string>('')

  const team = computed<Team | null>(() => store.getCurrentTeam)
  const isSaving = computed<boolean>(() => store.isSaving)
  const canManage = computed<boolean>(() => canManageTeams.value)
  const memberOptions = computed<AppSelectOption<number>[]>(() => toMemberOptions(store.getUsers))

  const snapshot = (): string => JSON.stringify({
    name: refName.value.trim(),
    members: [...refMembers.value].sort((a, b) => a - b),
  })

  const isSaveDisabled = computed<boolean>(() =>
    !canManage.value
    || !refName.value.trim()
    || refMembers.value.length === 0
    || snapshot() === original.value)

  const applyTeam = (loaded: Team): void => {
    refName.value = loaded.name ?? ''
    refMembers.value = (loaded.users ?? []).map(member => member.id)
    original.value = snapshot()
  }

  const load = async (): Promise<void> => {
    isNotFound.value = false
    // Clear any team left from a previous visit so the form stays gated (and hidden behind the
    // global loader) until this team's data arrives.
    store.setCurrentTeam(null)

    const result = await useTeamApi(teamId)

    if (!result) {
      isNotFound.value = true
      return
    }

    applyTeam(result.team)
  }

  const save = async (): Promise<void> => {
    const payload: TeamFormValues = {
      name: refName.value.trim(),
      users: refMembers.value,
    }

    const updated = await useTeamSave({ teamId, payload })

    if (updated) {
      store.setCurrentTeam(updated)
      applyTeam(updated)
    }
  }

  const askDelete = (): void => {
    isDeleteOpen.value = true
  }

  const cancelDelete = (): void => {
    isDeleteOpen.value = false
  }

  const confirmDelete = async (): Promise<void> => {
    const ok = await useTeamDelete(teamId)

    if (ok) {
      isDeleteOpen.value = false
      await router.push(AppRoute.Teams)
    }
  }

  return {
    refName,
    refMembers,
    memberOptions,
    team,
    isSaving,
    isNotFound,
    isSaveDisabled,
    canManage,
    isDeleteOpen,
    load,
    save,
    askDelete,
    cancelDelete,
    confirmDelete,
  }
}
